import type { IconProps } from "@tabler/icons-react";
import {
  IconBuildingOff,
  IconDeviceMobileOff,
  IconEyeOff,
  IconMapPinCog,
  IconPlayerPause,
  IconUserOff,
  IconMapPinOff,
  IconPackageOff,
  IconRoute,
  IconRulerOff,
  IconSteeringWheel,
  IconTagOff,
  IconTargetOff,
  IconTruckOff,
} from "@tabler/icons-react";
import type { ComponentType } from "react";
import { HealthCheckKey, HealthImpact } from "#/types/dataHealth";

/** Which page fixes a check's records, for the "Fix on …" label. */
export type FixPage =
  | "subClient"
  | "statuses"
  | "item"
  | "category"
  | "vehicleModel"
  | "vehicle"
  | "site"
  | "clientDetail";

interface HealthCheckMeta {
  icon: ComponentType<IconProps>;
  page: FixPage;
  /** The list page of the affected records, for the truncated note. Null for account settings. */
  listHref: string | null;
  /** The record's own page, for client users. Null when there is no such page. */
  recordHref: (id: number | string) => string | null;
  /** What `row.count` counts, when a row carries one. */
  countOf: "items" | "vehicles" | null;
}

export const CHECK_META: Record<HealthCheckKey, HealthCheckMeta> = {
  [HealthCheckKey.SUB_CLIENT_NO_COORDINATES]: {
    icon: IconMapPinOff,
    page: "subClient",
    listHref: "/sub-clients",
    recordHref: (id) => `/sub-clients/${id}`,
    countOf: null,
  },
  [HealthCheckKey.NO_AT_CUSTOMER_STATUS]: {
    icon: IconTagOff,
    page: "statuses",
    listHref: null,
    recordHref: () => null,
    countOf: null,
  },
  [HealthCheckKey.NO_INTERNAL_STATUS]: {
    icon: IconTagOff,
    page: "statuses",
    listHref: null,
    recordHref: () => null,
    countOf: null,
  },
  [HealthCheckKey.STATUS_NO_TYPE]: {
    icon: IconTagOff,
    page: "statuses",
    listHref: null,
    recordHref: () => null,
    countOf: "items",
  },
  [HealthCheckKey.ITEM_NO_CATEGORY]: {
    icon: IconPackageOff,
    page: "item",
    listHref: "/inventory/items",
    recordHref: (id) => `/inventory/items/${id}`,
    countOf: null,
  },
  [HealthCheckKey.SUB_CLIENT_NO_TARGET]: {
    icon: IconTargetOff,
    page: "subClient",
    listHref: "/sub-clients",
    recordHref: (id) => `/sub-clients/${id}`,
    countOf: null,
  },
  [HealthCheckKey.CATEGORY_NO_SPECS]: {
    icon: IconRulerOff,
    page: "category",
    listHref: "/inventory/categories",
    recordHref: (id) => `/inventory/categories/${id}`,
    countOf: null,
  },
  [HealthCheckKey.VEHICLE_MODEL_NO_CAPACITY]: {
    icon: IconTruckOff,
    page: "vehicleModel",
    listHref: "/fleet/models",
    recordHref: (id) => `/fleet/models/${id}`,
    countOf: "vehicles",
  },
  [HealthCheckKey.VEHICLE_NO_SITE]: {
    icon: IconSteeringWheel,
    page: "vehicle",
    listHref: "/fleet/vehicles",
    recordHref: (id) => `/fleet/vehicles/${id}`,
    countOf: null,
  },
  [HealthCheckKey.VEHICLE_NO_DRIVER]: {
    icon: IconUserOff,
    page: "vehicle",
    listHref: "/fleet/vehicles",
    recordHref: (id) => `/fleet/vehicles/${id}`,
    countOf: null,
  },
  [HealthCheckKey.SITE_NO_COORDINATES]: {
    icon: IconBuildingOff,
    page: "site",
    listHref: "/sites",
    recordHref: (id) => `/sites/${id}`,
    countOf: null,
  },
  [HealthCheckKey.DEVICE_NO_SITE]: {
    icon: IconDeviceMobileOff,
    page: "clientDetail",
    listHref: null,
    recordHref: () => null,
    countOf: null,
  },
  [HealthCheckKey.GEOFENCE_NOT_SET]: {
    icon: IconMapPinCog,
    page: "clientDetail",
    listHref: null,
    recordHref: () => null,
    countOf: null,
  },
  [HealthCheckKey.PRV_PAUSED]: {
    icon: IconPlayerPause,
    page: "clientDetail",
    listHref: null,
    recordHref: () => null,
    countOf: null,
  },
  [HealthCheckKey.NO_ACTIVE_USER]: {
    icon: IconUserOff,
    page: "clientDetail",
    listHref: null,
    recordHref: () => null,
    countOf: null,
  },
};

interface ImpactTheme {
  icon: ComponentType<IconProps>;
  /** Full Tailwind class strings, so the compiler sees them. */
  tile: string;
  tileSelected: string;
  iconWrap: string;
  count: string;
  badge: string;
  cardBorder: string;
}

export const IMPACT_THEME: Record<HealthImpact, ImpactTheme> = {
  [HealthImpact.VISIBILITY]: {
    icon: IconEyeOff,
    tile: "border-rose-200 bg-rose-50/60 hover:bg-rose-50",
    tileSelected: "ring-2 ring-rose-400 border-rose-300 bg-rose-50",
    iconWrap: "bg-rose-100 text-rose-700",
    count: "text-rose-700",
    badge: "bg-rose-100 text-rose-800",
    cardBorder: "border-l-rose-400",
  },
  [HealthImpact.PLANNING]: {
    icon: IconTargetOff,
    tile: "border-amber-200 bg-amber-50/60 hover:bg-amber-50",
    tileSelected: "ring-2 ring-amber-400 border-amber-300 bg-amber-50",
    iconWrap: "bg-amber-100 text-amber-800",
    count: "text-amber-800",
    badge: "bg-amber-100 text-amber-900",
    cardBorder: "border-l-amber-400",
  },
  [HealthImpact.VEHICLE]: {
    icon: IconTruckOff,
    tile: "border-violet-200 bg-violet-50/60 hover:bg-violet-50",
    tileSelected: "ring-2 ring-violet-400 border-violet-300 bg-violet-50",
    iconWrap: "bg-violet-100 text-violet-700",
    count: "text-violet-700",
    badge: "bg-violet-100 text-violet-800",
    cardBorder: "border-l-violet-400",
  },
  [HealthImpact.ORIGIN]: {
    icon: IconRoute,
    tile: "border-blue-200 bg-blue-50/60 hover:bg-blue-50",
    tileSelected: "ring-2 ring-blue-400 border-blue-300 bg-blue-50",
    iconWrap: "bg-blue-100 text-blue-700",
    count: "text-blue-700",
    badge: "bg-blue-100 text-blue-800",
    cardBorder: "border-l-blue-400",
  },
  [HealthImpact.ACCESS]: {
    icon: IconUserOff,
    tile: "border-fuchsia-200 bg-fuchsia-50/60 hover:bg-fuchsia-50",
    tileSelected: "ring-2 ring-fuchsia-400 border-fuchsia-300 bg-fuchsia-50",
    iconWrap: "bg-fuchsia-100 text-fuchsia-700",
    count: "text-fuchsia-700",
    badge: "bg-fuchsia-100 text-fuchsia-800",
    cardBorder: "border-l-fuchsia-400",
  },
};
